import {compose} from "mu-lambda";
import * as React from "react";

import {
    withActionScheduler,
    type WithActionSchedulerProps,
    type WithoutActionScheduler,
} from "@khanacademy/wonder-blocks-timing";
import {Errors, KAError} from "@repo/logging";

import withPrefersReducedMotion, {
    type WithoutPrefersReducedMotion,
    type WithPrefersReducedMotionProps,
} from "#data-access/with-prefers-reduced-motion.tsx";

type OwnProps = {
    isExpanded: boolean;
    children?: React.ReactNode;
};

// These extra props are provided automatically by the container component.
type Props = OwnProps &
    WithPrefersReducedMotionProps &
    WithActionSchedulerProps;

type State = {
    height: number | "auto";
    opacity: number;
    position: "static" | "absolute";
    enableTransition: boolean;
    showChildren: boolean;
};

/**
 * Expandable Component can expand and collapse.  The height of the children is
 * dynamically calculated before each transition so it can support child
 * elements that are added or removed.
 * Children are removed from DOM after collapse transition has completed
 */
class Expandable extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);

        const {isExpanded} = props;

        this.state = {
            showChildren: isExpanded,
            enableTransition: true,
            height: isExpanded ? "auto" : 0,
            opacity: isExpanded ? 1 : 0,
            position: "static",
        };
    }

    componentDidMount() {
        this._isMounted = true;
    }

    componentDidUpdate(prevProps: Props, prevState: State) {
        const {isExpanded} = this.props;

        if (prevProps.isExpanded !== isExpanded) {
            if (isExpanded) {
                this.expand();
            } else {
                this.collapse();
            }
        }
    }

    componentWillUnmount() {
        this._isMounted = false;
    }

    _isMounted = false;
    expanderContainer = React.createRef<HTMLDivElement>();
    // @ts-expect-error - TS2322 - Type 'Promise<void>' is not assignable to type 'Promise<undefined>'.
    transitionPromise: Promise<undefined> = Promise.resolve();

    getHeight: () => number = () =>
        this.expanderContainer.current
            ? this.expanderContainer.current.getBoundingClientRect().height
            : 0;

    /** Return the transition delay, in seconds. */
    getTransitionSpeed: () => number = () =>
        this.props.prefersReducedMotion ? 0 : 0.3;

    afterTransition: () => Promise<void> = () => {
        return new Promise((resolve) => {
            // TODO(jeff, WEB-1378): Use Wonder Blocks Timing API.
            // Dangling timer could fire after component unmounts!
            // eslint-disable-next-line no-restricted-syntax
            setTimeout(() => {
                resolve();
            }, this.getTransitionSpeed() * 1000);
        });
    };

    setStateAndWaitForAnimationFrames: (
        state: Partial<State>,
    ) => Promise<void> = (state: Partial<State>): Promise<void> => {
        return new Promise<void>((resolve, reject) => {
            if (!this._isMounted) {
                return reject(new KAError("unmounted", Errors.Internal));
            }

            // @ts-expect-error - TS2345 - Argument of type 'Partial<State>' is not assignable to parameter of type 'State | ((prevState: Readonly<State>, props: Readonly<Props>) => State | Pick<State, keyof State> | null) | Pick<...> | null'.
            this.setState(state, () => {
                this.props.schedule.animationFrame(() => {
                    this.props.schedule.animationFrame(() => {
                        resolve();
                    });
                });
            });
        }).catch(() => {
            //empty catch to handle case where component has been unmounted
        });
    };

    getMaxContainerHeight: () => Promise<number> = (): Promise<number> => {
        return new Promise((resolve) => {
            this.setStateAndWaitForAnimationFrames({
                height: "auto",
                opacity: 0,
                position: "absolute",
                enableTransition: false,
                showChildren: true,
            }).then(() => {
                resolve(this.getHeight());
            });
        });
    };

    expand: () => void = (): void => {
        let height;

        // @ts-expect-error - TS2322 - Type 'Promise<void | undefined>' is not assignable to type 'Promise<undefined>'.
        this.transitionPromise = this.transitionPromise
            .then(this.getMaxContainerHeight)
            .then((maxHeight) => (height = maxHeight))
            .then(() => {
                return this.setStateAndWaitForAnimationFrames({
                    height: 0,
                    opacity: 0,
                    position: "static",
                    enableTransition: false,
                });
            })
            .then(() => {
                return this.setStateAndWaitForAnimationFrames({
                    height,
                    opacity: 1,
                    enableTransition: true,
                });
            })
            .then(this.afterTransition)
            .then(() =>
                this.setStateAndWaitForAnimationFrames({
                    height: "auto",
                }),
            );
    };

    collapse: () => void = (): void => {
        const height = this.getHeight();

        // @ts-expect-error - TS2322 - Type 'Promise<void | undefined>' is not assignable to type 'Promise<undefined>'.
        this.transitionPromise = this.transitionPromise
            .then(() =>
                this.setStateAndWaitForAnimationFrames({
                    height,
                    opacity: 1,
                    position: "static",
                    enableTransition: false,
                    showChildren: true,
                }),
            )
            .then(() =>
                this.setStateAndWaitForAnimationFrames({
                    height: 0,
                    opacity: 0,
                    enableTransition: true,
                }),
            )
            .then(this.afterTransition)
            .then(() =>
                this.setStateAndWaitForAnimationFrames({
                    showChildren: false,
                }),
            );
    };

    render(): React.ReactNode {
        const {children} = this.props;
        const {showChildren, height, opacity, position, enableTransition} =
            this.state;

        const speed = this.getTransitionSpeed();

        const style = {
            height,
            opacity,
            position,
            transitionFunction: `ease-in-out, ease-in-out`,
            transitionDuration: `${speed}s, ${speed}s`,
            transitionProperty: enableTransition
                ? "height, opacity"
                : "none !important",
            overflow: "hidden",
        } as const;

        return showChildren ? (
            <div style={style} ref={this.expanderContainer}>
                {children}
            </div>
        ) : null;
    }
}

type ExportProps = WithoutActionScheduler<
    WithoutPrefersReducedMotion<
        JSX.LibraryManagedAttributes<
            typeof Expandable,
            React.ComponentProps<typeof Expandable>
        >
    >
>;

export default compose(
    withPrefersReducedMotion,
    withActionScheduler,
)(Expandable) as React.ComponentType<ExportProps>;
